/**
 * ChamadoDetailPage — detalhe do chamado com timeline (B1.6 spec §5.2).
 *
 * URL: /cliente/chamado/:id
 *
 * Backend GET /api/v1/cliente/chamado/:id retorna SOMENTE comentários
 * com visivel_cliente=1 (filtro server-side — Regra inviolável #7).
 *
 * Layout:
 *   - Header com número humano + título + status badge + data
 *   - Descrição original do chamado
 *   - Foto (se houver) — presigned URL R2 sob demanda
 *   - Timeline de comentários (visíveis ao cliente)
 *   - Form pra adicionar comentário cliente
 *
 * NÃO REVERTER:
 *   - Backend filtra visivel_cliente=1 — frontend NÃO precisa filtrar
 *   - Cross-cliente leak prevention: backend 404 quando chamado é doutro
 *     cliente (defesa via clientContext header)
 *   - Foto via presigned URL (zero exposição backend → R2 público)
 *   - Comentário cliente SEMPRE visivel_cliente=1 (não tem toggle)
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert, Badge, Button } from '@/components/ui';
import { PortalPage, PortalCarregando } from '@/components/cliente/PortalPage';
import { useClienteAuth } from '@/contexts/ClienteAuthContext';
import {
  faseLabel as resolveFaseLabel,
  faseBadgeVariant,
  useAddComentarioChamado,
  useChamadoDetail,
  useChamadoFotoUrl,
  type ChamadoComentario,
} from '@/lib/cliente-portal-store';
import { mensagemErro } from '@/lib/mensagem-publica';

import { formatarData } from '@/lib/format-date';
export function ChamadoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useClienteAuth();
  const { data, isLoading, isError, error } = useChamadoDetail(id);
  const addComentario = useAddComentarioChamado(id ?? '');
  const fotoUrlQuery = useChamadoFotoUrl(id, data?.fotoR2Key ?? null);
  const [comentario, setComentario] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleAddComentario(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (comentario.trim().length < 1) return;
    try {
      await addComentario.mutateAsync({ corpo: comentario.trim() });
      setComentario('');
    } catch (err) {
      setErrorMsg(mensagemErro(err, 'Não conseguimos enviar sua mensagem agora. Tente de novo.'));
    }
  }

  return (
    <PortalPage
      testId="chamado-detail-page"
      crumb="Meus chamados"
      title={data ? data.titulo : 'Chamado'}
      subtitle={
        data ? (
          <span className="flex flex-wrap items-center gap-2">
            {data.numeroHumano != null && (
              <span className="font-mono tabular-nums">#{data.numeroHumano}</span>
            )}
            <FaseBadge fase={data.fase} />
            <span>Aberto em {formatIso(data.criadoEmIso)}</span>
          </span>
        ) : undefined
      }
      actions={
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/cliente/meus-chamados')}
        >
          Voltar aos chamados
        </Button>
      }
    >
      {isLoading ? (
        <PortalCarregando linhas={2} rotulo="Carregando o chamado…" />
      ) : isError ? (
        <Alert variant="danger">
          {mensagemErro(
            error,
            'Não conseguimos abrir este chamado agora. Tente atualizar a página.',
          )}
        </Alert>
      ) : !data ? null : (
        <>
          {/* Descrição */}
          {data.descricao && (
            <section className="mb-6 rounded-xl border border-line bg-paper-surface p-5 dark:border-line-dark dark:bg-paper-dark-surface">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                O que você relatou
              </h2>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-1 dark:text-ink-dark-1">
                {data.descricao}
              </p>
            </section>
          )}

          {/* Foto */}
          {data.fotoR2Key && (
            <section className="mb-6" data-testid="chamado-foto-section">
              <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
                Foto anexada
              </h2>
              {fotoUrlQuery.isLoading ? (
                <p className="text-sm text-ink-2 dark:text-ink-dark-2">Carregando a foto…</p>
              ) : fotoUrlQuery.data?.url ? (
                <a
                  href={fotoUrlQuery.data.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Abrir a foto em tamanho maior"
                >
                  <img
                    src={fotoUrlQuery.data.url}
                    alt="Foto anexada ao chamado"
                    className="max-h-96 cursor-zoom-in rounded-lg border border-line hover:opacity-95 dark:border-line-dark"
                  />
                </a>
              ) : (
                <Alert variant="warning">
                  A foto não abriu agora. Atualize a página para tentar de novo.
                </Alert>
              )}
            </section>
          )}

          {/* Timeline */}
          <section className="mb-6" data-testid="chamado-timeline">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-ink-dark-3">
              Conversa ({data.comentarios.length})
            </h2>
            {data.comentarios.length === 0 ? (
              <p className="rounded-xl border border-dashed border-line px-4 py-5 text-sm text-ink-2 dark:border-line-dark dark:text-ink-dark-2">
                Ainda sem resposta. A equipe da IGM foi avisada e responde por aqui.
              </p>
            ) : (
              <div className="space-y-3">
                {data.comentarios.map((c) => (
                  <ComentarioItem key={c.id} comentario={c} isSelf={user?.id === c.autorId} />
                ))}
              </div>
            )}
          </section>

          {/* Add comentário */}
          <section className="rounded-xl border border-line bg-paper-surface p-5 dark:border-line-dark dark:bg-paper-dark-surface">
            <h2 className="mb-3 text-base font-semibold text-ink-1 dark:text-ink-dark-1">
              Quer acrescentar alguma coisa?
            </h2>
            <form onSubmit={handleAddComentario}>
              <label htmlFor="chamado-comentario" className="sr-only">
                Sua mensagem sobre este chamado
              </label>
              <textarea
                id="chamado-comentario"
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Ex.: o problema voltou hoje de manhã."
                rows={3}
                maxLength={1000}
                disabled={addComentario.isPending}
                className="block w-full rounded-xl border border-line bg-paper-surface px-3 py-2 text-sm focus:border-red focus:outline-none focus:ring-2 focus:ring-red-soft dark:border-line-dark dark:bg-paper-dark-elevated"
                data-testid="chamado-comentario-input"
              />
              {errorMsg && (
                <Alert variant="danger" className="mt-3">
                  {errorMsg}
                </Alert>
              )}
              <div className="mt-3 flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full sm:w-auto"
                  disabled={addComentario.isPending || comentario.trim().length === 0}
                  data-testid="chamado-comentario-submit"
                >
                  {addComentario.isPending ? 'Enviando…' : 'Enviar mensagem'}
                </Button>
              </div>
            </form>
          </section>
        </>
      )}
    </PortalPage>
  );
}

function ComentarioItem({
  comentario,
  isSelf,
}: {
  comentario: ChamadoComentario;
  isSelf: boolean;
}) {
  // Comentário com autorNome resolvido pelo backend (LEFT JOIN users).
  // isSelf compara autorId com user atual do ClienteAuth — true = "Você".
  // Caso contrário, assume "IGM Security" (lado interno) — comentários
  // visivel_cliente=1 doutro user cliente do mesmo cliente são raros mas
  // possíveis (admin que cadastrou múltiplos gestores). Mostra nome real.
  return (
    <div
      className={`rounded-xl border p-4 ${
        isSelf
          ? 'border-line bg-paper-warm dark:border-line-dark dark:bg-paper-dark-warm'
          : 'border-line bg-paper-surface dark:border-line-dark dark:bg-paper-dark-surface'
      }`}
      data-testid={`comentario-${comentario.id}`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-ink-1 dark:text-ink-dark-1">
          {isSelf ? 'Você' : comentario.autorNome}
        </span>
        {!isSelf && (
          <Badge variant="info" size="sm">
            IGM
          </Badge>
        )}
        <span className="ml-auto text-xs text-ink-2 dark:text-ink-dark-2">
          {formatIso(comentario.criadoEmIso)}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-1 dark:text-ink-dark-1">
        {comentario.corpo}
      </p>
    </div>
  );
}

function FaseBadge({ fase }: { fase: string }) {
  return (
    <Badge variant={faseBadgeVariant(fase)} size="sm">
      {resolveFaseLabel(fase)}
    </Badge>
  );
}

function formatIso(iso: string): string {
  try {
    return formatarData(iso, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
